/**
 * Detection for the booking-portal "minimum spend" policy.
 *
 * Policy `type` values are owned by the booking-portal API and are not enumerated in this
 * repo, so matching is deliberately loose: a candidate matches when its letters-only form
 * contains `minimumspend` or `minspend`. That covers `MinimumSpend`, `Minimum Spend`,
 * `minimum_spend`, `MIN_SPEND`, and friends.
 */

const MIN_SPEND_TOKENS = ['minimumspend', 'minspend'];

function normalize(value: string): string {
	return value.toLowerCase().replace(/[^a-z]/g, '');
}

function matches(value: unknown): boolean {
	if (typeof value !== 'string') {
		return false;
	}
	const normalized = normalize(value);
	return MIN_SPEND_TOKENS.some((token) => normalized.includes(token));
}

/**
 * Localized fields arrive either as a plain string, a `{ en: '...', fr: '...' }` map, or a
 * JSON-encoded map. Return every string worth testing so an English title still matches
 * during a French-language session.
 *
 * Encoded maps need no decoding: `matches` strips everything but letters, so the raw
 * `{"en":"Minimum spend","fr":"Dépense minimale"}` already normalizes to a string
 * containing `minimumspend`. That covers the fenced and inline-JSON forms too.
 */
function candidates(value: unknown): string[] {
	if (typeof value === 'string') {
		return [value];
	}
	if (value && typeof value === 'object') {
		return Object.values(value as Record<string, unknown>).filter(
			(entry): entry is string => typeof entry === 'string'
		);
	}
	return [];
}

function policyMatches(policy: unknown): boolean {
	if (!policy || typeof policy !== 'object') {
		return false;
	}
	const { type, name, displays } = policy as {
		type?: unknown;
		name?: unknown;
		displays?: unknown;
	};

	const fields: unknown[] = [type, name];
	if (Array.isArray(displays)) {
		for (const display of displays) {
			if (display && typeof display === 'object') {
				const { title, label } = display as { title?: unknown; label?: unknown };
				fields.push(title, label);
			}
		}
	}

	return fields.some((field) => candidates(field).some(matches));
}

export function hasMinimumSpendPolicy(policies?: unknown): boolean {
	return Array.isArray(policies) && policies.some(policyMatches);
}
